import { addMonths, periodContaining } from '@fd/finance-core';
import type { Transaction } from '@fd/schemas';
import { AlertCircle, AlertTriangle, Bell, Info, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { today, useBudgets, useEntities, useHousehold, usePersons, useRecurring, useSettlementContext } from '../app/data.ts';
import { useT } from '../app/i18n.tsx';
import { notificationMarks, readNotificationSettings, writeNotificationSettings } from '../app/notification-store.ts';
import { collectNotifications, type AppNotification, type NotificationSettings } from '../app/notifications.ts';
import { hrefFor } from '../app/router.tsx';
import { isAutoSettled } from '../app/auto-settle.ts';
import { settlementIn } from '../app/settlement-scope.ts';
import { useTheme } from '../app/theme.tsx';
import { useBankConnections } from '../pages/Banks.tsx';
import { AmountInput } from '../ui/AmountInput.tsx';
import { Card, Money } from '../ui/primitives.tsx';
import { categoryLabel } from '../ui/TransactionRow.tsx';

// The bell in the top bar: what needs the owner's eye (app/notifications.ts), newest first, with
// read and hidden marks kept on this device; optionally the system's own notifications too.

const SETTINGS_EVENT = 'fd:notification-settings';

/** Settings changed on the Settings page: the bell recounts at once. */
function notificationSettingsChanged(): void {
  window.dispatchEvent(new Event(SETTINGS_EVENT));
}

function useNotificationSettings(): NotificationSettings {
  const [settings, setSettings] = useState(readNotificationSettings);
  useEffect(() => {
    const update = () => setSettings(readNotificationSettings());
    window.addEventListener(SETTINGS_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(SETTINGS_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return settings;
}

function useNotifications(): AppNotification[] {
  const t = useT();
  const on = today();
  const settings = useNotificationSettings();
  const household = useHousehold();
  const budgets = useBudgets(on);
  const { items: recurring } = useRecurring();
  const transactions = useEntities<Transaction>('transaction');
  const settlementData = useSettlementContext();
  const connections = useBankConnections();

  const settlement = useMemo(() => {
    if (!settings.settlement || !settlementData || !household) return null;
    const month = addMonths(`${on.slice(0, 8)}01`, -1);
    const result = settlementIn(settlementData, 'period', periodContaining('MONTH', month));
    const ledger = result.ledgers.find((l) => l.currency === household.baseCurrency && l.suggestedTransfers.length > 0) ?? result.ledgers.find((l) => l.suggestedTransfers.length > 0);
    return ledger ? { month, currency: ledger.currency, transfers: ledger.suggestedTransfers } : null;
  }, [settings.settlement, settlementData, household, on]);

  return useMemo(() => {
    if (!household) return [];
    const base = household.baseCurrency;
    return collectNotifications({
      today: on,
      settings,
      baseCurrency: base,
      budgets: (budgets?.views ?? []).map((v) => ({
        key: v.budget.id,
        name: v.category ? categoryLabel(t, v.category) : (v.group?.name ?? null),
        month: v.status.month,
        spentMinor: v.status.spentMinor,
        limitMinor: v.status.limitMinor,
        usedBasisPoints: v.status.usedBasisPoints,
        currency: budgets!.currency,
      })),
      recurring: recurring.map((r) => ({ ...r, name: r.displayName, kind: r.shownKind })),
      expenses: (transactions.data ?? []).flatMap((tx) => {
        if (tx.deletedAt !== null || tx.kind !== 'EXPENSE' || (tx.status !== 'BOOKED' && tx.status !== 'PENDING')) return [];
        const minor = tx.currency === base ? BigInt(tx.amountMinor) : tx.baseAmountMinor !== null ? BigInt(tx.baseAmountMinor) : null;
        if (minor === null || minor >= 0n) return [];
        return [{ id: tx.id, name: tx.merchantName ?? tx.descriptionRaw ?? '', bookingDate: tx.bookingDate, amountMinor: -minor }];
      }),
      settlement,
      settled: (settlementData?.settlements ?? [])
        .filter((s) => s.deletedAt === null && s.status === 'SETTLED' && isAutoSettled(s))
        .map((s) => ({
          id: s.id,
          settledOn: s.settledOn,
          month: `${(s.periodStart ?? s.periodEnd).slice(0, 8)}01`,
          currency: s.currency,
          transfers: s.transfers.map((tr) => ({ fromPersonId: tr.fromPersonId, toPersonId: tr.toPersonId, amountMinor: BigInt(tr.amountMinor) })),
        })),
      banks: connections.map((c) => ({
        id: c.id,
        bank: [...new Set(c.accounts.filter((a) => a.status === 'LINKED' && a.visible !== false).map((a) => a.institutionName).filter((n): n is string => Boolean(n)))].join(', ') || c.institutionName,
        status: c.status,
        reauthorizationRequired: c.reauthorizationRequired,
        consentExpiresAt: c.consentExpiresAt,
      })),
    });
  }, [household, on, settings, budgets, recurring, transactions.data, settlement, settlementData, connections, t]);
}

/** A sentence with amounts in it: "{spent} z {limit}", the amounts as money (privacy mode hides them). */
function Sentence({ text, amounts }: { text: string; amounts: Record<string, { minor: bigint; currency: string }> }) {
  const parts = text.split(/\{(\w+)\}/);
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) return <Fragment key={i}>{part}</Fragment>;
        const amount = amounts[part];
        return amount ? <Money key={i} minor={amount.minor} currency={amount.currency} /> : <Fragment key={i}>{`{${part}}`}</Fragment>;
      })}
    </>
  );
}

function useDescribe() {
  const t = useT();
  const persons = usePersons(true);
  const person = (id: string) => persons.find((p) => p.id === id)?.displayName ?? '—';
  const keep = (...names: string[]) => Object.fromEntries(names.map((n) => [n, `{${n}}`]));
  /** The words of a notification, amounts left as {placeholders} for Sentence. */
  return (n: AppNotification): { text: string; amounts: Record<string, { minor: bigint; currency: string }> } => {
    switch (n.kind) {
      case 'budgetOver':
      case 'budgetNear': {
        const key = `notifications.${n.kind}${n.name === null ? 'All' : ''}`;
        return { text: t.dynamic(key, { name: n.name ?? '', ...keep('spent', 'limit') }), amounts: { spent: { minor: n.spentMinor, currency: n.currency }, limit: { minor: n.limitMinor, currency: n.currency } } };
      }
      case 'paymentLate':
      case 'incomeLate':
        return { text: t.t(`notifications.${n.kind}`, { name: n.name, date: t.date(n.dueDate, 'dayMonth') }), amounts: {} };
      case 'paymentHigher':
        return { text: t.t('notifications.paymentHigher', { name: n.name, ...keep('amount', 'usual') }), amounts: { amount: { minor: n.amountMinor, currency: n.currency }, usual: { minor: n.usualMinor, currency: n.currency } } };
      case 'largeExpense':
        return { text: t.t('notifications.largeExpense', { name: n.name, ...keep('amount') }), amounts: { amount: { minor: n.amountMinor, currency: n.currency } } };
      case 'settleUp':
      case 'settled':
        return {
          text: t.t(n.kind === 'settled' ? 'notifications.settled' : 'notifications.settleUp', { month: t.date(n.month, 'monthYear'), from: person(n.fromPersonId), to: person(n.toPersonId), ...keep('amount') }),
          amounts: { amount: { minor: n.amountMinor, currency: n.currency } },
        };
      case 'bankRenew':
        return { text: t.t('notifications.bankRenew', { bank: n.bank, date: t.date(n.until, 'dayMonth') }), amounts: {} };
      case 'bankReconnect':
        return { text: t.t('notifications.bankReconnect', { bank: n.bank }), amounts: {} };
    }
  };
}

const ICON = { bad: AlertCircle, warn: AlertTriangle, info: Info } as const;

/** Settings → Notifications: what to be told about (this device), the large-expense amount, system notifications. */
export function NotificationSettingsCard() {
  const t = useT();
  const household = useHousehold();
  const settings = useNotificationSettings();
  const [amount, setAmount] = useState(() => (Number(settings.largeFromMinor) / 100).toString());
  const permission = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  const save = (patch: Partial<NotificationSettings>) => {
    writeNotificationSettings({ ...settings, ...patch });
    notificationSettingsChanged();
  };
  const flag = (name: 'budgets' | 'payments' | 'large' | 'settlement' | 'banks') => (
    <label className="switch">
      <input type="checkbox" checked={settings[name]} onChange={(e) => save({ [name]: e.target.checked })} />
      <span aria-hidden="true" />
    </label>
  );
  // The switch always at the row's right; anything more (the large expense's amount) under the description.
  const row = (name: 'budgets' | 'payments' | 'large' | 'settlement' | 'banks', extra?: ReactNode) => (
    <div className="settings-row">
      <div className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span className="settings-row__label">{t.t(`notifications.settings.${name}`)}</span>
        <span className="list-row__meta">{t.t(`notifications.settings.${name}Hint`)}</span>
        {extra}
      </div>
      <div className="settings-row__control">{flag(name)}</div>
    </div>
  );
  async function toggleSystem(on: boolean) {
    if (!on) return save({ system: false });
    if (typeof Notification === 'undefined') return;
    const answer = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
    // Nothing shown now for what is already there: only what comes later.
    if (answer === 'granted') notificationMarks.markShown(readAllKeys.current);
    save({ system: answer === 'granted' });
  }
  const all = useNotifications();
  const readAllKeys = useRef<string[]>([]);
  readAllKeys.current = all.map((n) => n.key);
  return (
    <Card title={t.t('notifications.title')} className="notify-settings">
      <div className="settings-list">
        {row('budgets')}
        {row('payments')}
        {row(
          'large',
          <span className="notify__amount">
            <AmountInput
              className="input"
              currency={household?.baseCurrency}
              aria-label={t.t('notifications.settings.largeHint')}
              value={amount}
              disabled={!settings.large}
              onValue={setAmount}
              onBlur={() => {
                const value = Number(amount.replace(',', '.'));
                if (Number.isFinite(value) && value > 0) save({ largeFromMinor: BigInt(Math.round(value * 100)) });
                else setAmount((Number(settings.largeFromMinor) / 100).toString());
              }}
            />
            <span className="list-row__meta">{household?.baseCurrency ?? ''}</span>
          </span>,
        )}
        {row('settlement')}
        {row('banks')}
        <div className="settings-row">
          <div className="stack" style={{ gap: 2, minWidth: 0 }}>
            <span className="settings-row__label">{t.t('notifications.settings.system')}</span>
            <span className="list-row__meta">
              {permission === 'unsupported'
                ? t.t('notifications.settings.systemUnsupported')
                : permission === 'denied'
                  ? t.t('notifications.settings.systemBlocked')
                  : t.t('notifications.settings.systemHint')}
            </span>
          </div>
          <div className="settings-row__control">
            <label className="switch">
              <input type="checkbox" disabled={permission === 'unsupported' || permission === 'denied'} checked={settings.system && permission === 'granted'} onChange={(e) => void toggleSystem(e.target.checked)} />
              <span aria-hidden="true" />
            </label>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function NotificationBell() {
  const t = useT();
  const all = useNotifications();
  const describe = useDescribe();
  const { privacy } = useTheme();
  const settings = useNotificationSettings();
  const [open, setOpen] = useState(false);
  const [marks, setMarks] = useState(() => ({ read: notificationMarks.read(), hidden: notificationMarks.hidden() }));
  const box = useRef<HTMLDivElement>(null);
  const items = all.filter((n) => !marks.hidden.has(n.key));
  const unread = items.filter((n) => !marks.read.has(n.key));
  const refresh = () => setMarks({ read: notificationMarks.read(), hidden: notificationMarks.hidden() });

  // Closes on a click outside and on Escape.
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  // The system's notifications (the owner turned them on and the browser allowed): each once.
  const unreadKeys = unread.map((n) => n.key).join('|');
  useEffect(() => {
    if (!settings.system || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const shown = notificationMarks.shown();
    const fresh = unread.filter((n) => !shown.has(n.key));
    if (fresh.length === 0) return;
    for (const n of fresh.slice(0, 3)) {
      const { text, amounts } = describe(n);
      const plain = text.replace(/\{(\w+)\}/g, (_, name: string) => (privacy || !amounts[name] ? '•••' : t.money({ minor: amounts[name]!.minor, currency: amounts[name]!.currency })));
      try {
        const shownNote = new Notification(t.t('notifications.title'), { body: plain, tag: n.key });
        shownNote.onclick = () => {
          window.focus();
          location.assign(hrefFor(n.route));
        };
      } catch {
        // Some browsers allow notifications only from a service worker; the bell still shows them.
      }
    }
    notificationMarks.markShown(fresh.map((n) => n.key));
  }, [unreadKeys, settings.system]);

  return (
    <div className="notify" ref={box}>
      <button
        className="icon-btn notify__bell"
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread.length > 0 ? t.plural('notifications.unread', unread.length) : t.t('notifications.title')}
        title={t.t('notifications.title')}
        onClick={() => setOpen(!open)}
      >
        <Bell />
        {unread.length > 0 && <span className="notify__badge">{unread.length > 9 ? '9+' : unread.length}</span>}
      </button>
      {open && (
        <div className="notify__panel" role="dialog" aria-label={t.t('notifications.title')}>
          <div className="notify__head">
            <strong>{t.t('notifications.title')}</strong>
            {unread.length > 0 && (
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => {
                  notificationMarks.markRead(items.map((n) => n.key));
                  refresh();
                }}
              >
                {t.t('notifications.markAllRead')}
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="notify__empty">{t.t('notifications.empty')}</p>
          ) : (
            <ul className="notify__list">
              {items.map((n) => {
                const Icon = ICON[n.tone];
                const { text, amounts } = describe(n);
                const isNew = !marks.read.has(n.key);
                return (
                  <li key={n.key} className={`notify__item notify__item--${n.tone}${isNew ? ' notify__item--new' : ''}`}>
                    <Icon size={16} aria-hidden="true" className="notify__icon" />
                    <a
                      className="notify__text"
                      href={hrefFor(n.route)}
                      onClick={() => {
                        notificationMarks.markRead([n.key]);
                        refresh();
                        setOpen(false);
                      }}
                    >
                      <span>
                        <Sentence text={text} amounts={amounts} />
                      </span>
                      <span className="notify__when">{t.date(n.date, 'dayMonth')}</span>
                    </a>
                    <button
                      className="icon-btn notify__hide"
                      type="button"
                      aria-label={t.t('notifications.hide')}
                      title={t.t('notifications.hide')}
                      onClick={() => {
                        notificationMarks.hide(n.key);
                        refresh();
                      }}
                    >
                      <X size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <a className="notify__settings" href={hrefFor({ page: 'settings', section: 'notifications' })} onClick={() => setOpen(false)}>
            {t.t('notifications.settingsLink')}
          </a>
        </div>
      )}
    </div>
  );
}

